import { z } from 'zod';
import uiConfig from './ui';
import type { ExtraFeatureSchema, FeatureGroupSchema } from '@/schemas/filtersSchema';

export type ExtraFeaturesType = z.infer<typeof ExtraFeatureSchema>;
export type FeatureGroupIdType = z.infer<typeof FeatureGroupSchema>;

export interface FeatureGroupFiltersType {
  sale: FilterViewType[];
  rental: FilterViewType[];
}
export interface SortingOptionIdsType {
  sale: string[];
  rental: string[];
}
export type ListingTypesType = keyof typeof uiConfig.LISTING_TYPES;

export interface FeatureGroupType {
  id: FeatureGroupIdType;
  name: string;
  types: ListingTypesType[];
  filters: FeatureGroupFiltersType;
  extraFeatures: ExtraFeaturesType[];
  sortingOptionIds: SortingOptionIdsType;
  showInHousingOnSale: boolean;
  showInRentals: boolean;
  pathName: string;
  preferSiteArea?: boolean;
}
// Categories for managing the filter visibilities in the UI.
// Some differences to the filters in the URL
export type FilterViewType =
  | 'siteOwnership'
  | 'location'
  | 'priceMin'
  | 'priceMax'
  | 'livingAreaMin'
  | 'livingAreaMax'
  | 'numberOfRooms'
  | 'condition'
  | 'listingTypes'
  | 'extraFeatures'
  | 'yearMin'
  | 'yearMax'
  | 'siteAreaMin'
  | 'siteAreaMax'
  | 'propertyId'
  | 'showings'
  | 'rentMin'
  | 'rentMax'
  | 'totalAreaMin'
  | 'totalAreaMax'
  | 'energy';

const featureGroups: Record<string, FeatureGroupType> = {
  // Feature groups for main listing categories
  // Note: ids are backend API query keys and should not be modified.
  // Each listing has one or more of these ids in 'listing.featureGroups'
  HOUSING: {
    id: 'apartment',
    name: 'Asunnot',
    types: [89, 90, 91, 92, 93, 112, 113, 470],
    filters: {
      sale: [
        'siteOwnership',
        'location',
        'priceMin',
        'priceMax',
        'livingAreaMin',
        'livingAreaMax',
        'numberOfRooms',
        'condition',
        'listingTypes',
        'extraFeatures',
        'yearMin',
        'yearMax',
        'propertyId',
        'showings',
        'energy',
      ],
      rental: [
        'location',
        'rentMin',
        'rentMax',
        'livingAreaMin',
        'livingAreaMax',
        'numberOfRooms',
        'condition',
        'listingTypes',
        'extraFeatures',
        'yearMin',
        'yearMax',
        'propertyId',
        'showings',
      ],
    },
    extraFeatures: ['elevator', 'sauna', 'balcony', 'shore', 'sharedShore'],
    sortingOptionIds: {
      sale: ['newest', 'oldest', 'lowestPrice', 'highestPrice', 'smallestLivingArea', 'largestLivingArea'],
      rental: ['newest', 'oldest', 'lowestRent', 'highestRent', 'smallestLivingArea', 'largestLivingArea'],
    },
    showInHousingOnSale: true,
    showInRentals: false,
    pathName: 'asunnot',
  },
  RENTALS: {
    // TODO RENTALS group should be deprecated and merged with HOUSING
    id: 'rentalApartment',
    name: 'Asunnot',
    types: [89, 90, 91, 92, 93, 112, 113, 470],
    filters: {
      sale: [
        'siteOwnership',
        'location',
        'priceMin',
        'priceMax',
        'livingAreaMin',
        'livingAreaMax',
        'numberOfRooms',
        'condition',
        'listingTypes',
        'extraFeatures',
        'yearMin',
        'yearMax',
        'propertyId',
        'showings',
      ],
      rental: [
        'location',
        'rentMin',
        'rentMax',
        'livingAreaMin',
        'livingAreaMax',
        'numberOfRooms',
        'condition',
        'listingTypes',
        'extraFeatures',
        'yearMin',
        'yearMax',
        'propertyId',
        'showings',
      ],
    },
    extraFeatures: ['elevator', 'sauna', 'balcony'],
    sortingOptionIds: {
      sale: ['newest', 'oldest', 'lowestPrice', 'highestPrice', 'smallestLivingArea', 'largestLivingArea'],
      rental: ['newest', 'oldest', 'lowestRent', 'highestRent', 'smallestLivingArea', 'largestLivingArea'],
    },
    showInHousingOnSale: false,
    showInRentals: true,
    pathName: 'asunnot',
  },
  NEW_HOUSING: {
    id: 'newHousing',
    name: 'Uudiskohteet',
    types: [89, 90, 91, 92, 93, 112, 113, 470],
    filters: {
      sale: [
        'siteOwnership',
        'location',
        'priceMin',
        'priceMax',
        'livingAreaMin',
        'livingAreaMax',
        'numberOfRooms',
        'condition',
        'listingTypes',
        'extraFeatures',
        'yearMin',
        'yearMax',
        'propertyId',
        'showings',
        'energy',
      ],
      rental: [
        'location',
        'rentMin',
        'rentMax',
        'livingAreaMin',
        'livingAreaMax',
        'numberOfRooms',
        'condition',
        'listingTypes',
        'extraFeatures',
        'yearMin',
        'yearMax',
        'propertyId',
        'showings',
      ],
    },
    extraFeatures: ['elevator', 'sauna', 'balcony', 'shore', 'sharedShore'],
    sortingOptionIds: {
      sale: ['newest', 'oldest', 'lowestPrice', 'highestPrice', 'smallestLivingArea', 'largestLivingArea'],
      rental: ['newest', 'oldest', 'lowestRent', 'highestRent', 'smallestLivingArea', 'largestLivingArea'],
    },
    showInHousingOnSale: true,
    showInRentals: false,
    pathName: 'uudiskohteet',
  },
  VACATION_HOUSING: {
    id: 'vacationHousing',
    name: 'Loma-asunnot',
    types: [96, 471, 89, 90, 91, 92, 93, 112, 113, 470],
    filters: {
      sale: [
        'siteOwnership',
        'location',
        'priceMin',
        'priceMax',
        'livingAreaMin',
        'livingAreaMax',
        'numberOfRooms',
        'condition',
        'listingTypes',
        'extraFeatures',
        'yearMin',
        'yearMax',
        'propertyId',
        'showings',
        'energy',
      ],
      rental: [
        'location',
        'rentMin',
        'rentMax',
        'livingAreaMin',
        'livingAreaMax',
        'numberOfRooms',
        'condition',
        'listingTypes',
        'extraFeatures',
        'yearMin',
        'yearMax',
        'propertyId',
        'showings',
      ],
    },
    extraFeatures: ['elevator', 'sauna', 'balcony', 'shore', 'sharedShore'],
    sortingOptionIds: {
      sale: ['newest', 'oldest', 'lowestPrice', 'highestPrice', 'smallestLivingArea', 'largestLivingArea'],
      rental: ['newest', 'oldest', 'lowestRent', 'highestRent', 'smallestLivingArea', 'largestLivingArea'],
    },
    showInHousingOnSale: true,
    showInRentals: true,
    pathName: 'loma-asunnot',
  },
  SITES: {
    id: 'site',
    name: 'Tontit',
    types: [108, 104, 95, 107],
    filters: {
      sale: [
        'location',
        'priceMin',
        'priceMax',
        'siteAreaMin',
        'siteAreaMax',
        'listingTypes',
        'extraFeatures',
        'propertyId',
        'showings',
      ] as FilterViewType[],
      rental: [
        'location',
        'rentMin',
        'rentMax',
        'siteAreaMin',
        'siteAreaMax',
        'listingTypes',
        'extraFeatures',
        'propertyId',
        'showings',
      ],
    },
    extraFeatures: ['shore', 'sharedShore'],
    sortingOptionIds: {
      sale: ['newest', 'oldest', 'lowestPrice', 'highestPrice', 'smallestSiteArea', 'largestSiteArea'],
      rental: ['newest', 'oldest', 'lowestRent', 'highestRent', 'smallestSiteArea', 'largestSiteArea'],
    },
    preferSiteArea: true,
    showInHousingOnSale: true,
    showInRentals: false,
    pathName: 'tontit',
  },
  FARMS: {
    id: 'farm',
    name: 'Maa- ja metsätilat',
    types: [105, 106, 109],
    filters: {
      sale: [
        'siteOwnership',
        'location',
        'priceMin',
        'priceMax',
        'siteAreaMin',
        'siteAreaMax',
        'listingTypes',
        'extraFeatures',
        'propertyId',
        'showings',
        'energy',
      ],
      rental: [
        'siteOwnership',
        'location',
        'rentMin',
        'rentMax',
        'siteAreaMin',
        'siteAreaMax',
        'listingTypes',
        'extraFeatures',
        'propertyId',
        'showings',
      ],
    },
    extraFeatures: ['sauna', 'balcony', 'shore', 'sharedShore'],
    sortingOptionIds: {
      sale: ['newest', 'oldest', 'lowestPrice', 'highestPrice', 'smallestSiteArea', 'largestSiteArea'],
      rental: ['newest', 'oldest', 'lowestRent', 'highestRent', 'smallestSiteArea', 'largestSiteArea'],
    },
    preferSiteArea: true,
    showInHousingOnSale: true,
    showInRentals: false,
    pathName: 'maa-ja-metsatilat',
  },
  OFFICES: {
    id: 'office',
    name: 'Toimitilat',
    types: [115, 116, 117, 108, 118],
    filters: {
      sale: [
        'siteOwnership',
        'location',
        'priceMin',
        'priceMax',
        'totalAreaMin',
        'totalAreaMax',
        'listingTypes',
        'propertyId',
        'showings',
        'energy',
      ],
      rental: [
        'siteOwnership',
        'location',
        'rentMin',
        'rentMax',
        'totalAreaMin',
        'totalAreaMax',
        'listingTypes',
        'propertyId',
        'showings',
      ],
    },
    extraFeatures: ['elevator', 'sauna', 'balcony'],
    sortingOptionIds: {
      sale: ['newest', 'oldest', 'lowestPrice', 'highestPrice', 'smallestTotalArea', 'largestTotalArea'],
      rental: ['newest', 'oldest', 'lowestRent', 'highestRent', 'smallestTotalArea', 'largestTotalArea'],
    },
    showInHousingOnSale: true,
    showInRentals: true,
    pathName: 'toimitilat',
  },
  GARAGES_AND_STORAGES: {
    id: 'storageAndGarage',
    name: 'Autotallit ja varastot',
    types: [94, 472, 473, 110, 111, 114],
    filters: {
      sale: [
        'siteOwnership',
        'location',
        'priceMin',
        'priceMax',
        'totalAreaMin',
        'totalAreaMax',
        'listingTypes',
        'propertyId',
        'showings',
        'energy',
      ],
      rental: [
        'location',
        'rentMin',
        'rentMax',
        'totalAreaMin',
        'totalAreaMax',
        'listingTypes',
        'propertyId',
        'showings',
      ],
    },
    extraFeatures: [],
    sortingOptionIds: {
      sale: [
        'siteOwnership',
        'newest',
        'oldest',
        'lowestPrice',
        'highestPrice',
        'smallestTotalArea',
        'largestTotalArea',
      ],
      rental: ['newest', 'oldest', 'lowestRent', 'highestRent', 'smallestTotalArea', 'largestTotalArea'],
    },
    showInHousingOnSale: true,
    showInRentals: true,
    pathName: 'autotallit-ja-varastot',
  },
};

export default featureGroups;

export const getFeatureGroupById = (id: string | undefined) => {
  return id ? Object.values(featureGroups).find((group) => group.id === id) : undefined;
};
